import {Component, ElementRef, EventEmitter, Input, OnDestroy, Output, PLATFORM_ID, ViewChild, inject} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {FormControl} from '@angular/forms';
import {SmModalStackService} from './sm-modal-stack.service';

@Component({
  selector: 'sm-confirmation-dialog',
  templateUrl: './sm-confirmation-dialog.component.html',
  styleUrls: ['./sm-confirmation-dialog.component.scss', './sm-confirmation-dialog-review.scss', './sm-confirmation-dialog-form.scss',
    './sm-confirmation-dialog-notice.scss'],
  standalone: false,
})
export class SmConfirmationDialogComponent implements OnDestroy {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly modalStack = inject(SmModalStackService);
  private static nextTitleId = 0;
  private _isOpen = false;
  private activeNativeDialog?: HTMLDialogElement;
  private anchorObserver?: ResizeObserver;
  private readonly updateAnchor = () => this.positionWithinAnchor();
  @Input() centerWithin: HTMLElement | null = null;
  anchorInsets: {top: number; right: number; bottom: number; left: number} | null = null;

  @Input()
  set isOpen(value: boolean) {
    const nextValue = Boolean(value);
    const wasOpen = this._isOpen;
    this._isOpen = nextValue;

    if (!nextValue) {
      if (this.activeNativeDialog) this.modalStack.closed(this.activeNativeDialog);
      this.activeNativeDialog?.close();
      this.stopPositioning();
    }

    if (nextValue && !wasOpen) {
      this.resetState();
    }
  }

  get isOpen(): boolean {
    return this._isOpen;
  }

  @Input() title = '';
  @Input() description = '';
  @Input() confirmationText = 'RESET';
  @Input() confirmationPrompt = '';
  @Input() confirmationPlaceholder = '';
  @Input() confirmationMismatchLabel = '';
  @Input() textInputLabel = '';
  @Input() textInputPlaceholder = '';
  @Input() textInputInputMode: 'text' | 'numeric' | 'decimal' = 'text';
  @Input() textInputRequired = false;
  @Input() textInputMultiline = false;
  @Input() cancelLabel = '';
  @Input() alternateLabel = '';
  @Input() confirmLabel = '';
  @Input() closeLabel = '';
  @Input() backdropDismiss = true;
  @Input() busy = false;
  @Input() confirmDisabled = false;
  @Input() confirmTone: 'danger' | 'primary' | 'warning' = 'danger';
  @Input() contentAlign: 'start' | 'center' = 'center';
  @Input() actionsAlign: 'end' | 'center' = 'center';
  @Input() compactActions = false;
  @Input() panelVariant: 'default' | 'wide' | 'notice' | 'review' | 'form' = 'default';
  @Input() expandToViewport = false;
  @Input() panelWidth: string | null = null;
  @Input() fullScreenOnMobile = false;
  @Input() error = '';
  @Input() dialogType: 'default' | 'information' | 'warning' | 'danger' = 'default';
  @Input() heroIconName = '';
  @Input() showCloseButton = false;
  @Input() showCancelButton = true;
  @Input() showAlternateButton = false;
  @Input() showConfirmButton = true;

  @Output() cancelRequest = new EventEmitter<void>();
  @Output() alternateRequest = new EventEmitter<void>();
  @Output() confirmRequest = new EventEmitter<string>();

  get usesNativeDialog(): boolean {
    return this.panelVariant === 'review' || this.panelVariant === 'form';
  }

  @ViewChild('nativeDialog') set nativeDialog(ref: ElementRef<HTMLDialogElement> | undefined) {
    if (!this.isBrowser) return;
    const dialog = ref?.nativeElement;
    this.activeNativeDialog = dialog;
    if (!dialog) return;
    // The browser's modal top layer supplies focus containment and an inert background.
    queueMicrotask(() => {
      if (this.activeNativeDialog !== dialog || !this.isOpen || !dialog.isConnected || dialog.open) return;
      dialog.showModal();
      this.modalStack.opened(dialog);
      this.startPositioning();
      dialog.querySelector<HTMLElement>('h2')?.focus({preventScroll: true});
    });
  }

  @ViewChild('nonNativeDialog') set nonNativeDialog(ref: ElementRef<HTMLElement> | undefined) {
    if (ref) this.modalStack.openedOverlay(this, () => this.handleCancel());
    else this.modalStack.closedOverlay(this);
  }

  ngOnDestroy(): void {
    this.modalStack.closedOverlay(this);
    this.stopPositioning();
    if (this.activeNativeDialog) this.modalStack.closed(this.activeNativeDialog);
    this.activeNativeDialog?.close();
    this.activeNativeDialog = undefined;
  }

  private startPositioning(): void {
    if (!this.isBrowser || !this.centerWithin) return;
    this.stopPositioning();
    this.anchorObserver = new ResizeObserver(this.updateAnchor);
    this.anchorObserver.observe(this.centerWithin);
    const content = this.centerWithin.closest('ion-content');
    if (content) this.anchorObserver.observe(content);
    window.addEventListener('resize', this.updateAnchor);
    document.addEventListener('scroll', this.updateAnchor, true);
    window.visualViewport?.addEventListener('resize', this.updateAnchor);
    this.positionWithinAnchor();
  }

  private stopPositioning(): void {
    if (!this.isBrowser) return;
    this.anchorObserver?.disconnect();
    this.anchorObserver = undefined;
    window.removeEventListener('resize', this.updateAnchor);
    document.removeEventListener('scroll', this.updateAnchor, true);
    window.visualViewport?.removeEventListener('resize', this.updateAnchor);
    this.anchorInsets = null;
  }

  private positionWithinAnchor(): void {
    if (!this.isBrowser || !this.centerWithin || !this.isOpen) return;
    const rect = this.centerWithin.getBoundingClientRect();
    const content = this.centerWithin.closest('ion-content')?.getBoundingClientRect();
    const viewport = window.visualViewport;
    const margin = 14;
    const top = Math.max(margin, content?.top ?? 0, viewport?.offsetTop ?? 0);
    const bottom = Math.min(window.innerHeight - margin, content?.bottom ?? window.innerHeight,
      (viewport?.offsetTop ?? 0) + (viewport?.height ?? window.innerHeight));
    this.anchorInsets = {
      left: Math.max(margin, rect.left),
      right: Math.max(margin, window.innerWidth - rect.right),
      top,
      bottom: Math.max(margin, window.innerHeight - Math.max(top, bottom)),
    };
  }

  handleNativeCancel(event: Event): void {
    event.preventDefault();
    this.handleCancel();
  }

  readonly confirmationControl = new FormControl<string>('', {nonNullable: true});
  readonly textInputControl = new FormControl<string>('', {nonNullable: true});
  readonly titleId = `sm-confirmation-dialog-title-${++SmConfirmationDialogComponent.nextTitleId}`;

  get requiresTypedConfirmation(): boolean {
    return this.confirmationText.trim().length > 0;
  }

  get canConfirm(): boolean {
    if (this.busy || this.confirmDisabled) {
      return false;
    }

    if (!this.requiresTypedConfirmation) {
      return !this.textInputRequired || this.textInputControl.value.trim().length > 0;
    }

    return this.confirmationControl.value === this.confirmationText
      && (!this.textInputRequired || this.textInputControl.value.trim().length > 0);
  }

  get hasTextInput(): boolean {
    return this.textInputLabel.trim().length > 0 || this.textInputPlaceholder.trim().length > 0;
  }

  get showMismatchError(): boolean {
    if (!this.requiresTypedConfirmation) {
      return false;
    }

    return this.confirmationControl.value.length > 0 && this.confirmationControl.value !== this.confirmationText;
  }

  get hasHeroIcon(): boolean {
    return this.heroIconName.trim().length > 0;
  }

  handleCancel(): void {
    if (this.busy) {
      return;
    }

    this.resetState();
    this.cancelRequest.emit();
  }

  handleBackdropClick(): void {
    if (!this.backdropDismiss) {
      return;
    }

    this.handleCancel();
  }

  handleConfirm(): void {
    if (!this.canConfirm) {
      return;
    }

    this.confirmRequest.emit(this.textInputControl.value.trim());
  }

  handleAlternate(): void {
    if (!this.busy) {
      this.alternateRequest.emit();
    }
  }

  private resetState(): void {
    this.confirmationControl.setValue('', {emitEvent: false});
    this.textInputControl.setValue('', {emitEvent: false});
  }
}
